import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import authService from '../../../lib/authService';
import LeftPanel from './LeftPanel';
import { pushCleverTapEvent } from '../pushCleverTapEvent';

const schema = yup.object({
  password: yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain uppercase, lowercase, number and special character'),
  confirmPassword: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match')
});

const PasswordSetup = ({ formData, token, onNext, onLoginClick, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    resolver: yupResolver(schema)
  });

  const password = watch('password', '');

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Set the password
      const response = await authService.resetPassword(token, data.password);
      setSuccess('Profile created successfully! Logging you in...');

      // Step 2: Automatically login the user with their credentials
      try {
        // Use a dummy recaptcha token for signup auto-login
        // In production, you might want to handle this differently
        const loginResponse = await authService.loginDetails(
          formData.email,
          data.password,
          '' // Empty recaptcha token for auto-login after signup
        );

        const accessToken = loginResponse?.result?.access_token;

        if (accessToken) {
          // Store the auth token
          localStorage.setItem('credlix_auth_token', accessToken);

          // Get user profile
          const profileResponse = await authService.getUserProfile(accessToken);
          window.gtag('event', 'sign_up')
          pushCleverTapEvent("sign_up",{email: formData.email,'PageName': 'Sign_up'});
          // Pass login data to parent component
          setTimeout(() => {
            if (onNext) {
              onNext({
                formData,
                passwordSet: true,
                response,
                autoLogin: true,
                loginData: {
                  email: formData.email,
                  token: accessToken,
                  profile: profileResponse
                }
              });
            }
          }, 1500);
        } else {
          // If auto-login fails, just show success notification
          setTimeout(() => {
            if (onNext) {
              onNext({
                formData,
                passwordSet: true,
                response,
                showSuccessNotification: true
              });
            }
          }, 2000);
        }
      } catch (loginErr) {
        console.error('Auto-login failed:', loginErr);
        // If auto-login fails, just show success notification
        setTimeout(() => {
          if (onNext) {
            onNext({
              formData,
              passwordSet: true,
              response,
              showSuccessNotification: true
            });
          }
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (!password) return { strength: 0, text: '', color: '' };

    let strength = 0;
    if (password.length >= 6) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    const levels = [
      { strength: 0, text: '', color: '' },
      { strength: 1, text: 'Very Weak', color: 'bg-red-500' },
      { strength: 2, text: 'Weak', color: 'bg-orange-500' },
      { strength: 3, text: 'Fair', color: 'bg-yellow-500' },
      { strength: 4, text: 'Good', color: 'bg-blue-500' },
      { strength: 5, text: 'Strong', color: 'bg-green-500' }
    ];

    return levels[strength];
  };

  const passwordStrength = getPasswordStrength();
  const maskedNumber = formData.mobileNumber ?
    `......${formData.mobileNumber.slice(-3)}` :
    '......442';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[20px] shadow-xl w-full max-w-3xl flex overflow-hidden">
        <LeftPanel />

        {/* Right Panel */}
        <div className="w-[55%] p-8 relative sm:w-full sm:p-3">
          <button
            onClick={() => onLoginClick && onLoginClick()}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="max-w-md mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 sm:text-lg">Enter OTP</h3>
            <div className="flex items-center text-gray-600 mb-4 sm:mb-1 sm:text-sm">
              <span>OTP sent to register {maskedNumber}</span>
              <button className="ml-2 text-brand font-medium hover:underline">
                Edit
              </button>
            </div>

            {/* OTP Display (filled) */}
            <div className="flex justify-center space-x-3 mb-2">
              {[2, 2, 2, 2, 2, 2].map((digit, index) => (
                <div
                  key={index}
                  className="w-6 h-6 sm:w-5 sm:h-5 sm:text-sm text-center text-base border-2 border-green-500 bg-green-50 text-green-700 rounded-lg flex items-center justify-center"
                >
                  {digit}
                </div>
              ))}
            </div>

            <div className="text-[#0A7205] rounded-lg mb-2 flex items-center justify-end text-sm">
              <svg className="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Number Verified!
            </div>

            <h4 className="text-lg font-bold text-black mb-1 sm:text-base">Set Password</h4>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="block text-sm font-semibold text-[#3C3C3C] mb-xsp">
                  Enter Password*
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    placeholder="Enter password"
                    className="text-sm w-full px-2 py-1 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {password && (
                  <div className="mt-xsp">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-[7px]">
                        <div
                          className={`h-[7px] rounded-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{passwordStrength.text}</span>
                    </div>
                  </div>
                )}
                {errors.password && (
                  <p className="text-red-500 text-sm mt-xsp">{errors.password.message}</p>
                )}
              </div>

              <div className='mt-1'>
                <label className="block text-sm font-semibold text-[#3C3C3C] mb-xsp">
                  Confirm Password*
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirmPassword')}
                    placeholder="Re-enter password"
                    className="text-sm w-full px-2 py-1 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || success}
                className="mt-2 w-full bg-brand text-white p-1 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-brand focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Setting Password...
                  </div>
                ) : success ? (
                  'Success! Closing...'
                ) : (
                  'Continue'
                )}
              </button>
            </form>

            {error && (
              <div className="text-red-700 rounded-lg my-1 text-sm">
                {error}
              </div>
            )}

            {/* {success && (
              <div className="text-[#0A7205] rounded-md mb-2 flex items-center text-sm font-medium border border-[0A7205] px-1 py-xsp bg-[#DFFDDF]">
                <svg className="w-3 h-3 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Profile created successfully
              </div>
            )} */}

            <div className="mt-2 text-center sm:mt-1">
              <span className="text-[#979797] text-sm">Already have an account? </span>
              <button
                onClick={onLoginClick}
                className="text-brand font-medium hover:underline"
              >
                Login Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordSetup;
